import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
    Plus, Search, MoreHorizontal, Pencil, Trash2, Building2
} from 'lucide-react';

const INDUSTRY_OPTIONS = [
    'Technology', 'Healthcare', 'Finance', 'Legal', 'Education',
    'Manufacturing', 'Retail', 'Government', 'Other'
];

export const MSPClientsPage = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [deletingClient, setDeletingClient] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', contact_name: '', contact_email: '', industry: '', notes: '' });
    const navigate = useNavigate();

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const result = await getClients();
            setClients(Array.isArray(result) ? result : result.items || []);
        } catch (err) {
            toast.error('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const openAddDialog = () => {
        setEditingClient(null);
        setForm({ name: '', contact_name: '', contact_email: '', industry: '', notes: '' });
        setDialogOpen(true);
    };

    const openEditDialog = (client) => {
        setEditingClient(client);
        setForm({
            name: client.name || '',
            contact_name: client.contact_name || '',
            contact_email: client.contact_email || '',
            industry: client.industry || '',
            notes: client.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Client name is required');
            return;
        }
        setSaving(true);
        try {
            if (editingClient) {
                await updateClient(editingClient.id, form);
                toast.success('Client updated successfully');
            } else {
                await createClient(form);
                toast.success('Client created successfully');
            }
            setDialogOpen(false);
            fetchClients();
        } catch (err) {
            toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingClient) return;
        try {
            await deleteClient(deletingClient.id);
            toast.success('Client deleted successfully');
            setDeleteDialogOpen(false);
            setDeletingClient(null);
            fetchClients();
        } catch (err) {
            toast.error('Failed to delete client');
        }
    };

    const filteredClients = clients.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="clients-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="msp-clients-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
                        Client Companies
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your client organizations
                    </p>
                </div>
                <Button onClick={openAddDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                </Button>
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                />
            </div>

            {/* Table */}
            {filteredClients.length === 0 ? (
                <Card className="border-border shadow-sm">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium text-foreground mb-2">
                            {search ? 'No clients match your search' : 'No clients yet'}
                        </p>
                        {!search && (
                            <Button onClick={openAddDialog} className="mt-2">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Client
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-border shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Industry</TableHead>
                                <TableHead className="text-center">Devices</TableHead>
                                <TableHead className="text-center">Alerts</TableHead>
                                <TableHead className="w-[50px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredClients.map((client) => (
                                <TableRow
                                    key={client.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => navigate(`/msp/clients/${client.id}`)}
                                >
                                    <TableCell className="font-medium">{client.name}</TableCell>
                                    <TableCell>
                                        <div>
                                            <p className="text-sm">{client.contact_name || '-'}</p>
                                            <p className="text-xs text-muted-foreground">{client.contact_email || ''}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {client.industry ? (
                                            <Badge variant="secondary" className="text-xs">{client.industry}</Badge>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell className="text-center">{client.device_count || 0}</TableCell>
                                    <TableCell className="text-center">{client.alert_count || 0}</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(client); }}>
                                                    <Pencil className="w-4 h-4 mr-2" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="text-destructive"
                                                    onClick={(e) => { e.stopPropagation(); setDeletingClient(client); setDeleteDialogOpen(true); }}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium text-foreground">Company Name *</label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Acme Corp"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Contact Name</label>
                            <Input
                                value={form.contact_name}
                                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                placeholder="John Doe"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Contact Email</label>
                            <Input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                placeholder="john@acme.com"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Industry</label>
                            <Select
                                value={form.industry}
                                onValueChange={(value) => setForm({ ...form, industry: value })}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select industry" />
                                </SelectTrigger>
                                <SelectContent>
                                    {INDUSTRY_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Notes</label>
                            <textarea
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="Additional notes..."
                                className="mt-1 w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : (editingClient ? 'Update' : 'Create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Client</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground py-4">
                        Are you sure you want to delete <span className="font-medium text-foreground">{deletingClient?.name}</span>?
                        This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MSPClientsPage;
